import { useState } from "react";
import type { Dispatch, SetStateAction } from "react";
import type { ApiClient, Auftrag, Unternehmen } from "@kundeninfo/api-client";
import { auftragKopffelder, heuteAlsTag, leererAuftragEntwurf,
         pruefeAuftrag, zeigeAuftragsFehlerFeld,
         zeigeAuftragsFeld } from "./AuftragDetail.js";
import type { AuftragEntwurf } from "./AuftragDetail.js";
import { useKopffaltung } from "./Kopffaltung.js";
import type { Kopffaltung } from "./Kopffaltung.js";
import { apiMeldung, feldBefundeAus, betragText } from "./vorgangsdetail.js";

/**
 * Das ANLEGEN eines Auftrags als wiederverwendbarer Hook (Bereichs-Spalten, Etappe 1,
 * Aufgabe 2, Fixrunde 1) — die Schwester von `useAngebotAnlegen` und aus demselben Grund
 * herausgelöst: Der Auftrags-Bereich (Aufgabe 4) benutzt ihn, statt ihn nachzubauen.
 *
 * Die Feinheit, die dabei sonst verloren ginge, ist die VORBELEGUNG aus einem angenommenen
 * Angebot (Ruling P3A-B, Empfängerhälfte): Sie füllt den Entwurf, trägt beim Speichern die
 * `offerId` — und danach geht der frisch angelegte Auftrag GLEICH ins Bearbeiten. Seine
 * Positionen sind schon darin (der Server kopiert sie mit); ohne diesen Schritt sähe der
 * Bearbeiter nach dem Ableiten nur eine Listenzeile und müsste raten, ob überhaupt etwas
 * übernommen wurde. Frei angelegt bleibt die Liste stehen wie bisher.
 */

/** Die Vorbelegung aus einem angenommenen Angebot. KundenAkte bildet sie aus dem
 *  ACCEPTED-Angebot; hier kommt sie fertig an. */
export type Vorbelegung = {
  offerId: string;
  companyId: string;
  title: string;
  netAmount: number;
  contactId: string | null;
  responsibleUserId: string | null;
  /** Die Periodenregel des Herkunftsangebots (Festlegung Z10): Periodenregel und Raten
   *  sind EINE Vereinbarung, die Ableitung übernimmt beides. */
  periodBasis: "CALENDAR_YEAR" | "CONTRACT_YEAR";
};

/** Der Entwurf aus einer Angebots-Vorbelegung: Kopf- und Herkunftsdaten übernommen, die
 *  Laufzeit bleibt offen (die legt erst der Auftrag fest). Die TEXTE ebenso: Der Server
 *  übernimmt sie beim Ableiten aus dem Angebot mit — sie hier zusätzlich aus der Maske zu
 *  schicken hiesse, denselben Satz zweimal zu führen. */
export function auftragEntwurfAusVorbelegung(v: Vorbelegung): AuftragEntwurf {
  return {
    companyId: v.companyId,
    title: v.title,
    // Deutsch geschrieben, weil deutsch gelesen wird (siehe betragText/betragZahl).
    netAmount: betragText(v.netAmount),
    contactId: v.contactId ?? "",
    responsibleUserId: v.responsibleUserId ?? "",
    paymentTermId: "", customerPurchaseOrderNumber: "", orderedOn: heuteAlsTag(),
    startDate: "", endDate: "", openEnded: false, introText: "", outroText: "",
    termValue: "", termUnit: "MONTHS",
    minimumTermValue: "", minimumTermUnit: "MONTHS",
    renewalPeriodValue: "", renewalPeriodUnit: "MONTHS",
    noticePeriodValue: "", noticePeriodUnit: "MONTHS", noticeReference: "TERM_END",
    periodBasis: v.periodBasis,
  };
}

export type AuftragAnlegenClient = Pick<ApiClient, "createSalesOrder">;

export type AuftragAnlegen = {
  entwurf: AuftragEntwurf;
  setEntwurf: Dispatch<SetStateAction<AuftragEntwurf>>;
  feldFehler: string | null;
  /** Die Befunde JE FELD (ME-Etappe 3, Aufgabe 4) — Servername → Satz, am Feld gezeigt:
   *  aus der Prüfung der Maske und aus den 422-Befunden des Servers. */
  fehlerJeFeld: Readonly<Record<string, string>>;
  setFeldFehler: Dispatch<SetStateAction<string | null>>;
  /** Läuft gerade ein `createSalesOrder`? Sperrt Anlegen und Abbrechen. */
  speichert: boolean;
  /** Die offerId, mit der eine aus einem Angebot vorbelegte Anlage gespeichert wird —
   *  zugleich das Zeichen, dass die Maske als „Auftrag aus Angebot" auftritt. */
  vorbelegtOfferId: string | null;
  /**
   * Der Stand, mit dem die Maske aufgemacht hat — die Vergleichsgrundlage für die Rückfrage
   * vor dem Abbrechen (Hausregel „striktes Modalpanel", 06.09.2026). Bei einer Anlage AUS
   * EINEM ANGEBOT ist das der vorbelegte Entwurf und nicht der leere: Sonst meldete die
   * Maske schon beim Öffnen „ungespeicherte Änderungen", obwohl niemand etwas getippt hat.
   */
  ausgang: AuftragEntwurf;
  /**
   * Die Einfaltung des Kopfblocks — beim ANLEGEN aufgeklappt (Entscheid Dirk 08.09.2026).
   *
   * Am Auftrag wiegt das doppelt: Die Anlage AUS EINEM ANGEBOT bittet ausdrücklich darum,
   * „die Laufzeit noch zu ergänzen" — und die Laufzeitfelder stehen genau in diesem Block.
   * Zugeklappt wäre der Hinweis eine Aufforderung zu etwas, das man nicht sieht.
   */
  kopf: Kopffaltung;
  /** Entwurf, Herkunft, Feldmeldung und Einfaltung auf Anfang. Die Herkunft gehört zur
   *  ANLAGE; stünde sie beim Bearbeiten noch, zeigte die Maske den Vorbelegungs-Hinweis an
   *  einem längst angelegten Auftrag. */
  zuruecksetzen: () => void;
  anlegen: () => Promise<void>;
};

export function useAuftragAnlegen(arg: {
  client: AuftragAnlegenClient;
  kundeId: string;
  /** Die wählbaren Unternehmen — bei genau einem ist es vorgewählt. */
  companies: Unternehmen[];
  /**
   * Das Präfix der Feld-Kennungen der Maske, die diesen Hook benutzt („kauf-neu" in der
   * Akte, „bauf-neu" im Bereich) — er braucht es, um bei einem Feldbefund GENAU DAS Feld
   * zu zeigen (`kopf.zeigeFeld`). Die Prüfung nennt nur die Endung der Kennung.
   */
  feldPraefix: string;
  /** Optional: die Vorbelegung aus einem Angebot. Sie wirkt einmal, beim ersten Zeichnen —
   *  danach führt der Hook seinen Entwurf selbst. */
  vorbelegung?: Vorbelegung | null | undefined;
  /** Nach dem Anlegen: Der Aufrufer schliesst seine Anlage. */
  onSchliessen: () => void;
  /** Nach dem Anlegen: neu laden und die FRISCHE Liste zurückgeben (sie trägt die
   *  aufgelösten Namen). */
  onNeuLaden: () => Promise<Auftrag[]>;
  /** Aus einem Angebot abgeleitet: diesen Vorgang gleich ins Bearbeiten holen. */
  onBearbeiten: (auftrag: Auftrag, meldung: string) => void;
}): AuftragAnlegen {
  const [entwurf, setEntwurf] = useState<AuftragEntwurf>(() =>
    arg.vorbelegung != null
      ? auftragEntwurfAusVorbelegung(arg.vorbelegung)
      : leererAuftragEntwurf(arg.companies));
  const [ausgang, setAusgang] = useState<AuftragEntwurf>(() =>
    arg.vorbelegung != null
      ? auftragEntwurfAusVorbelegung(arg.vorbelegung)
      : leererAuftragEntwurf(arg.companies));
  const [vorbelegtOfferId, setVorbelegtOfferId] =
    useState<string | null>(arg.vorbelegung?.offerId ?? null);
  const [feldFehler, setFeldFehler] = useState<string | null>(null);
  const [fehlerJeFeld, setFehlerJeFeld] = useState<Readonly<Record<string, string>>>({});
  const [speichert, setSpeichert] = useState(false);
  const kopf = useKopffaltung(true);

  function zuruecksetzen(): void {
    const frisch = leererAuftragEntwurf(arg.companies);
    setEntwurf(frisch);
    setAusgang(frisch);
    setVorbelegtOfferId(null);
    setFeldFehler(null);
    setFehlerJeFeld({});
    kopf.zuruecksetzen();
  }

  async function anlegen(): Promise<void> {
    const problem = pruefeAuftrag(entwurf, true);
    if (problem !== null) {
      setFeldFehler(problem.meldung);
      setFehlerJeFeld({ [problem.feld]: problem.meldung });
      // Aufklappen und hineinsehen: Der Block ist beim Anlegen zwar offen, aber der
      // Bearbeiter kann ihn zugeklappt haben — dann stünde die Meldung über einem Feld,
      // das nicht zu sehen ist.
      zeigeAuftragsFeld(kopf, arg.feldPraefix, problem.feld);
      return;
    }
    setSpeichert(true);
    setFeldFehler(null);
    setFehlerJeFeld({});
    const ausAngebot = vorbelegtOfferId !== null;
    try {
      const angelegt = await arg.client.createSalesOrder(arg.kundeId, {
        companyId: entwurf.companyId, offerId: vorbelegtOfferId,
        ...auftragKopffelder(entwurf),
      });
      arg.onSchliessen();
      const frisch = await arg.onNeuLaden();
      // Bevorzugt der Satz aus der frisch geladenen Liste; fehlt er dort — etwa weil eine
      // Sicht ihn ausblendet — dient die Antwort selbst.
      if (ausAngebot) {
        const offen = frisch.find((a) => a.id === angelegt.id) ?? angelegt;
        arg.onBearbeiten(offen,
          "Aus dem Angebot abgeleitet — die Positionen sind übernommen; bitte die Laufzeit "
          + "ergänzen.");
      }
    } catch (e) {
      setFeldFehler(apiMeldung(e, "Der Auftrag konnte nicht angelegt werden."));
      setFehlerJeFeld(feldBefundeAus(e));
      // Die zweite Quelle von Feldbefunden: Der Server prüft mehr als die Maske
      // (Laufzeit-Konsistenz, Titel-Höchstlänge, Betragsgrenzen, Bestellnummer, fehlende
      // Kundenbeziehung). Nennt er ein Feld im Kopfblock, wird es sichtbar gemacht.
      zeigeAuftragsFehlerFeld(kopf, arg.feldPraefix, e);
    } finally {
      setSpeichert(false);
    }
  }

  return { entwurf, setEntwurf, feldFehler, setFeldFehler, fehlerJeFeld, speichert,
           vorbelegtOfferId,
           ausgang, kopf, zuruecksetzen, anlegen };
}
